// Inspired by react-hot-toast library
import React, { useState, useEffect } from 'react';

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;

interface ToastOptions {
	title?: string;
	description?: string;
	variant?:
		| 'default'
		| 'destructive'
		| 'success'
		| 'toast.probability.all-markets-traded'
		| 'toast.friend-request'
		| 'admin.snackbar'
		| 'admin.snackbar.error';
	action?: React.ReactNode;
	duration?: number;
}

interface Toast extends ToastOptions {
	id: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

interface ToastState {
	toasts: Toast[];
}

const actionTypes = {
	ADD_TOAST: 'ADD_TOAST',
	UPDATE_TOAST: 'UPDATE_TOAST',
	DISMISS_TOAST: 'DISMISS_TOAST',
	REMOVE_TOAST: 'REMOVE_TOAST',
} as const;

type ActionType = (typeof actionTypes)[keyof typeof actionTypes];

interface Action {
	type: ActionType;
	toast?: Partial<Toast> & { id: string };
	toastId?: string;
}

let count = 0;

function genId() {
	count = (count + 1) % Number.MAX_VALUE;
	return count.toString();
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
	if (toastTimeouts.has(toastId)) {
		return;
	}

	const timeout = setTimeout(() => {
		toastTimeouts.delete(toastId);
		dispatch({
			type: actionTypes.REMOVE_TOAST,
			toastId,
		});
	}, TOAST_REMOVE_DELAY);

	toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: ToastState, action: Action): ToastState => {
	switch (action.type) {
		case actionTypes.ADD_TOAST:
			return {
				...state,
				toasts: [action.toast as Toast, ...state.toasts].slice(0, TOAST_LIMIT),
			};

		case actionTypes.UPDATE_TOAST:
			return {
				...state,
				toasts: state.toasts.map((t) =>
					t.id === action.toast?.id ? { ...t, ...action.toast } : t
				),
			};

		case actionTypes.DISMISS_TOAST: {
			const { toastId } = action;

			if (toastId) {
				addToRemoveQueue(toastId);
			} else {
				state.toasts.forEach((toast) => {
					addToRemoveQueue(toast.id);
				});
			}

			return {
				...state,
				toasts: state.toasts.map((t) =>
					t?.id === toastId || toastId === undefined
						? {
								...t,
								open: false,
							}
						: t
				),
			};
		}
		case actionTypes.REMOVE_TOAST:
			if (action.toastId === undefined) {
				return {
					...state,
					toasts: [],
				};
			}
			return {
				...state,
				toasts: state.toasts.filter((t) => t?.id !== action.toastId),
			};
	}
};

const listeners: Array<React.Dispatch<React.SetStateAction<ToastState>>> = [];

let memoryState: ToastState = { toasts: [] };

function dispatch(action: Action) {
	memoryState = reducer(memoryState, action);
	listeners.forEach((listener) => {
		listener(memoryState);
	});
}

function toast({ ...props }: ToastOptions) {
	const id = genId();

	const update = (props: Partial<ToastOptions>) =>
		dispatch({
			type: actionTypes.UPDATE_TOAST,
			toast: { ...props, id },
		});

	const dismiss = () => dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id });

	dispatch({
		type: actionTypes.ADD_TOAST,
		toast: {
			...props,
			id,
			open: true,
			onOpenChange: (open: boolean) => {
				if (!open) dismiss();
			},
		},
	});

	return {
		id,
		dismiss,
		update,
	};
}

function useToast() {
	const [state, setState] = useState<ToastState>(memoryState);

	useEffect(() => {
		listeners.push(setState);
		return () => {
			const index = listeners.indexOf(setState);
			if (index > -1) {
				listeners.splice(index, 1);
			}
		};
	}, [state]);

	return {
		...state,
		toast,
		dismiss: (toastId?: string) => dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
	};
}

// --- Probability achievement toast ---

interface ProbabilityToastOptions {
	achievementType: string;
	botName?: string;
	onClick?: () => void;
	onShow?: () => void;
}

const probabilityListeners: Array<(t: ProbabilityToastOptions | null) => void> = [];
let currentProbabilityToast: ProbabilityToastOptions | null = null;

function addProbabilityListener(fn: (t: ProbabilityToastOptions | null) => void) {
	probabilityListeners.push(fn);
}

function removeProbabilityListener(fn: (t: ProbabilityToastOptions | null) => void) {
	const idx = probabilityListeners.indexOf(fn);
	if (idx > -1) probabilityListeners.splice(idx, 1);
}

const probabilityToast = {
	success(opts: ProbabilityToastOptions) {
		currentProbabilityToast = opts;
		probabilityListeners.forEach((fn) => fn(opts));
	},
	dismiss() {
		currentProbabilityToast = null;
		probabilityListeners.forEach((fn) => fn(null));
	},
};

(toast as any).probability = probabilityToast;

export function useProbabilityToast() {
	const [current, setCurrent] = useState<ProbabilityToastOptions | null>(currentProbabilityToast);

	useEffect(() => {
		addProbabilityListener(setCurrent);
		return () => removeProbabilityListener(setCurrent);
	}, []);

	return { toast: current, dismiss: probabilityToast.dismiss };
}

export { useToast, toast };
